import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';
import { isMissionsAuthError, isMissionsApiSuccess } from './missionsApi';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });
    const json = await res.json().catch(() => ({}));
    json.http_status = res.status;
    return json;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

const authFetchWithRetry = async (path, options = {}) => {
  let res = await authFetch(path, options);
  if (isMissionsAuthError(res)) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    res = await authFetch(path, options);
  }
  return res;
};

export const isDailyPageApiSuccess = isMissionsApiSuccess;

export const getDailyPagePayload = (res) => res?.data ?? null;

export const getDailyPageErrorMessage = (res, fallback) => {
  if (isMissionsAuthError(res)) {
    return 'Session token is invalid or expired. Log out, log in again, then reopen this page.';
  }
  const details = (Array.isArray(res?.data?.errors) ? res.data.errors : [])
    .map((error) => [error?.path, error?.message].filter(Boolean).join(': '))
    .filter(Boolean)
    .join(' ');
  const message = String(res?.message || '');
  if (details) return message ? `${message} ${details}` : details;
  return message || fallback;
};

/** GET /admin/daily-page/days?start_date=&end_date= */
export const listDailyPageDays = async ({ startDate, endDate } = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const query = params.toString();
  return authFetchWithRetry(`/admin/daily-page/days${query ? `?${query}` : ''}`, { method: 'GET' });
};

/** GET /admin/daily-page/days/:date */
export const getDailyPageDay = async (date) =>
  authFetchWithRetry(`/admin/daily-page/days/${date}`, { method: 'GET' });

/** GET /admin/daily-page/days/:date/preview */
export const getDailyPageDayPreview = async (date) =>
  authFetchWithRetry(`/admin/daily-page/days/${date}/preview`, { method: 'GET' });

/** POST /admin/daily-page/days/:date/activities */
export const createDailyPageActivity = async (date, body) =>
  authFetch(`/admin/daily-page/days/${date}/activities`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** PUT /admin/daily-page/activities/:id */
export const updateDailyPageActivity = async (id, body) =>
  authFetch(`/admin/daily-page/activities/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** DELETE /admin/daily-page/activities/:id */
export const deleteDailyPageActivity = async (id, { forceCorrection } = {}) => {
  const params = new URLSearchParams();
  if (forceCorrection) params.set('force_correction', 'true');
  const query = params.toString();
  return authFetch(`/admin/daily-page/activities/${id}${query ? `?${query}` : ''}`, {
    method: 'DELETE',
    ...(forceCorrection ? { body: JSON.stringify({ force_correction: true }) } : {}),
  });
};

/** POST /admin/daily-page/days/:date/publish */
export const publishDailyPageDay = async (date) =>
  authFetch(`/admin/daily-page/days/${date}/publish`, { method: 'POST', body: '{}' });

/** POST /admin/daily-page/days/:date/unpublish */
export const unpublishDailyPageDay = async (date) =>
  authFetch(`/admin/daily-page/days/${date}/unpublish`, { method: 'POST', body: '{}' });

/** PATCH /admin/daily-page/days/:date/mission-tasks — legacy bridge */
export const patchDailyPageMissionTasks = async (date, body) =>
  authFetch(`/admin/daily-page/days/${date}/mission-tasks`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** GET /admin/daily-page/day-config/:date */
export const getDailyPageDayConfig = async (date) =>
  authFetchWithRetry(`/admin/daily-page/day-config/${date}`, { method: 'GET' });

/** PUT /admin/daily-page/day-config/:date */
export const putDailyPageDayConfig = async (date, body) =>
  authFetch(`/admin/daily-page/day-config/${date}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** GET /admin/daily-page/weekly-milestones */
export const getWeeklyMilestones = async () =>
  authFetchWithRetry('/admin/daily-page/weekly-milestones', { method: 'GET' });

/** PUT /admin/daily-page/weekly-milestones */
export const putWeeklyMilestones = async (body) =>
  authFetch('/admin/daily-page/weekly-milestones', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** GET /admin/daily-page/wheel */
export const getWheelConfig = async () =>
  authFetchWithRetry('/admin/daily-page/wheel', { method: 'GET' });

/** PUT /admin/daily-page/wheel */
export const putWheelConfig = async (body) =>
  authFetch('/admin/daily-page/wheel', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** Feature flags used by Daily Tasks quick toggles */
export const listFeatureFlags = async () =>
  authFetchWithRetry('/admin/feature-flags', { method: 'GET' });

export const updateFeatureFlag = async (key, body) =>
  authFetch(`/admin/feature-flags/${key}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
